import React, { useEffect, useState, useRef } from 'react';
import { useGame } from '../context/GameContext';
import GameCard from '../components/GameCard';
import DeckSidebar from '../components/DeckSidebar';
import DivineDropOverlay from '../components/DivineDropOverlay';
import { bumpCardStat } from '../game/cardStats';
import { CARD_POOL_DIVINE, isDivineCard } from '../game/cards';
import { Radio } from 'lucide-react';

const VOTE_DURATION_SEC = 20;

export default function Reward() {
  const {
    pendingReward, takeRewardCard, skipReward,
    streamerMode, twitchStatus, chatVotes, resetChatVotes, run,
  } = useGame();
  const [tick, setTick] = useState(VOTE_DURATION_SEC);
  const [locked, setLocked] = useState(false);
  const [divineDrop, setDivineDrop] = useState(null); // { cardId }
  const startedAt = useRef(null);

  // Reset votes each time a new reward appears
  useEffect(() => {
    if (pendingReward) {
      resetChatVotes();
      startedAt.current = Date.now();
      setLocked(false);
      setTick(VOTE_DURATION_SEC);
      // v2.8 QoL: track SEEN for each displayed reward card
      try { (pendingReward.cards || []).forEach(cid => bumpCardStat(cid, 'seen')); } catch (e) {}
      // v2.9: detect divine drop and trigger overlay (v3.0: include class-locked divines)
      const divine = (pendingReward.cards || []).find(cid => isDivineCard(cid));
      if (divine) setDivineDrop({ cardId: divine });
      else setDivineDrop(null);
    }
  }, [pendingReward, resetChatVotes]);

  // Countdown when chat vote is active
  useEffect(() => {
    if (!streamerMode || !twitchStatus.connected || !pendingReward || locked) return;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - (startedAt.current || Date.now())) / 1000);
      const remaining = Math.max(0, VOTE_DURATION_SEC - elapsed);
      setTick(remaining);
      if (remaining === 0) {
        // Determine winner
        let winner = -1;
        let winnerCount = 0;
        for (const [k, set] of Object.entries(chatVotes)) {
          const c = set instanceof Set ? set.size : (Array.isArray(set) ? set.length : 0);
          if (c > winnerCount) { winnerCount = c; winner = parseInt(k, 10); }
        }
        if (winner >= 0 && winner < pendingReward.cards.length && !locked) {
          setLocked(true);
          setTimeout(() => takeRewardCard(pendingReward.cards[winner]), 1200);
        }
      }
    }, 500);
    return () => clearInterval(timer);
  }, [streamerMode, twitchStatus.connected, pendingReward, chatVotes, locked, takeRewardCard]);

  if (!pendingReward) return null;

  const totalVotes = Object.values(chatVotes).reduce((acc, s) => acc + (s instanceof Set ? s.size : (Array.isArray(s) ? s.length : 0)), 0);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12">
      <DeckSidebar deck={run?.player?.deck || []} />
      <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">VICTORY</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase text-[color:var(--neon)] mt-2">PICK YOUR SLOP</h1>
      <div className="text-[color:var(--pink)] mt-2 font-mono uppercase">+{pendingReward.gold} gold obtained</div>

      {streamerMode && twitchStatus.connected && (
        <div className="mt-4 brutal-border-pink px-4 py-2 flex items-center gap-3" data-testid="chat-vote-banner">
          <Radio size={16} className="animate-pulse text-[color:var(--pink)]"/>
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)]">
            CHAT VOTING · #{twitchStatus.channel} · <span data-testid="chat-vote-timer">{tick}s</span> · {totalVotes} votes
          </div>
        </div>
      )}

      <div className="mt-8 flex gap-6 flex-wrap justify-center" data-testid="reward-card-choices">
        {pendingReward.cards.map((cid, i) => {
          const setV = chatVotes[i];
          const count = setV instanceof Set ? setV.size : (Array.isArray(setV) ? setV.length : 0);
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          return (
            <div key={i} onClick={() => !locked && takeRewardCard(cid)} className="flex flex-col items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--pink)] font-mono">
                !{i + 1}
                {streamerMode && twitchStatus.connected && (
                  <span className="ml-2 text-[color:var(--neon)]" data-testid={`vote-count-${i}`}>· {count} ({pct}%)</span>
                )}
              </div>
              <GameCard cardId={cid} testid={`reward-card-${i}`} />
            </div>
          );
        })}
      </div>

      <button className="brutal-btn-pink brutal-btn mt-10" onClick={skipReward} disabled={locked} data-testid="skip-reward-button">
        SKIP →
      </button>
      {divineDrop && (
        <DivineDropOverlay cardId={divineDrop.cardId} onDone={() => setDivineDrop(null)} />
      )}
    </div>
  );
}
